import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

// FAQ item interface
interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: React.ReactNode;
}

const FaqPage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/faqs'];
  const structuredData = generateStructuredData('/faqs');
  
  // State for search and expanded panels
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | false>('booking');
  
  // Example FAQ data
  const faqItems: FaqItem[] = [
    {
      id: 'booking-1',
      category: 'booking',
      question: 'How far in advance should I book transportation?',
      answer: (
        <>
          <Typography paragraph>
            For standard bookings, we recommend booking at least 24 hours in advance to ensure availability. 
            However, for peak times, large group transfers, or specialized vehicle requests, 
            we suggest booking 3-5 days ahead.
          </Typography>
          <Typography>
            For urgent same-day service, please call our booking line directly at +91 6351003667 to check availability.
          </Typography>
        </>
      )
    },
    {
      id: 'booking-2',
      category: 'booking',
      question: 'How do I modify or cancel my booking?',
      answer: (
        <>
          <Typography paragraph>
            You can modify or cancel your booking by:
          </Typography>
          <Typography component="ol" sx={{ pl: 2 }}>
            <li>Logging into your account and selecting the booking you wish to change</li>
            <li>Calling our customer service team at +91 6351003667</li>
            <li>Emailing your request to contact.theunitedtravels@gmail.com</li>
          </Typography>
          <Typography paragraph>
            Please note our cancellation policy: full refund (minus 10% fee) for cancellations more than 24 hours before service, 
            50% refund for 12-24 hours notice, and no refund for less than 12 hours notice.
          </Typography>
        </>
      )
    },
    {
      id: 'booking-3',
      category: 'booking',
      question: 'Can I book transportation for a third party?',
      answer: (
        <Typography paragraph>
          Yes, you can book transportation for colleagues, clients, or guests. During the booking process, 
          you'll have the option to specify the passenger's details. We'll send the booking confirmation to both 
          you and the passenger unless you specify otherwise.
        </Typography>
      )
    },
    {
      id: 'services-1',
      category: 'services',
      question: 'What types of vehicles are available?',
      answer: (
        <>
          <Typography paragraph>
            Our fleet includes:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Luxury Sedans (Mercedes-Benz S-Class, BMW 7 Series, Tesla Model S)</li>
            <li>Premium SUVs (Cadillac Escalade, Lincoln Navigator)</li>
            <li>Executive Vans (Mercedes-Benz Sprinter)</li>
            <li>Mini Coaches and Buses (for larger groups)</li>
          </Typography>
          <Typography>
            Visit our <Link component={RouterLink} to="/fleet">Fleet page</Link> to view all vehicle options with specifications and photos.
          </Typography>
        </>
      )
    },
    {
      id: 'services-2',
      category: 'services',
      question: 'Do you provide airport meet and greet services?',
      answer: (
        <Typography paragraph>
          Yes, our airport meet and greet service includes having a professional chauffeur meet you at the 
          baggage claim area (for domestic flights) or outside customs (for international flights) with a 
          name sign. The chauffeur will assist with luggage and escort you to your waiting vehicle. 
          This service incurs an additional ₹2,490 fee but ensures a seamless airport experience.
        </Typography>
      )
    },
    {
      id: 'services-3',
      category: 'services',
      question: 'Can I request a specific driver?',
      answer: (
        <Typography paragraph>
          Yes, if you've had a positive experience with one of our chauffeurs, you can request them for 
          future bookings. While we cannot guarantee availability due to scheduling, we do our best to 
          accommodate driver preferences for our clients.
        </Typography>
      )
    },
    {
      id: 'payment-1',
      category: 'payment',
      question: 'What payment methods do you accept?',
      answer: (
        <>
          <Typography paragraph>
            We accept the following payment methods:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>All major credit and debit cards (Visa, Mastercard, American Express, Discover)</li>
            <li>Corporate accounts with net-30 terms (subject to approval)</li>
            <li>Digital wallets (Apple Pay, Google Pay)</li>
          </Typography>
          <Typography>
            For security reasons, we do not accept cash payments.
          </Typography>
        </>
      )
    },
    {
      id: 'payment-2',
      category: 'payment',
      question: 'How does pricing work for hourly bookings?',
      answer: (
        <Typography paragraph>
          For hourly bookings, you'll be charged based on the vehicle type and the number of hours reserved. 
          A minimum of 3 hours is required for all hourly bookings. If your actual usage exceeds the reserved time, 
          you'll be billed for additional time in 30-minute increments. Our chauffeurs track the exact usage time 
          from pickup to final drop-off.
        </Typography>
      )
    },
    {
      id: 'payment-3',
      category: 'payment',
      question: 'Are gratuities included in the price?',
      answer: (
        <Typography paragraph>
          A 20% gratuity is automatically added to all bookings. This ensures our chauffeurs are fairly compensated 
          for their professional service. If you wish to provide an additional tip based on exceptional service, 
          you may do so directly with the chauffeur or by contacting our customer service team after your trip.
        </Typography>
      )
    },
    {
      id: 'corporate-1',
      category: 'corporate',
      question: 'How do I set up a corporate account?',
      answer: (
        <>
          <Typography paragraph>
            Setting up a corporate account is easy:
          </Typography>
          <Typography component="ol" sx={{ pl: 2 }}>
            <li>Contact our corporate sales team at contact.theunitedtravels@gmail.com</li>
            <li>Complete a short application form</li>
            <li>Provide company details and billing information</li>
            <li>Set up user access for employees who will book transportation</li>
          </Typography>
          <Typography paragraph>
            Once approved, your company will receive dedicated account manager support, consolidated billing, 
            detailed reporting, and preferential rates.
          </Typography>
        </>
      )
    },
    {
      id: 'corporate-2',
      category: 'corporate',
      question: 'What reporting capabilities are available for corporate accounts?',
      answer: (
        <Typography paragraph>
          Corporate accounts receive comprehensive reporting that includes:
          detailed trip history, cost breakdown by department/employee, usage patterns, 
          expense categorization, and carbon footprint metrics. Reports can be customized 
          and scheduled for automatic delivery or accessed through your corporate dashboard.
        </Typography>
      )
    },
    {
      id: 'corporate-3',
      category: 'corporate',
      question: 'Do you offer volume discounts?',
      answer: (
        <Typography paragraph>
          Yes, we offer tiered pricing based on your company's transportation volume. 
          Our corporate clients typically save 10-25% compared to standard rates, 
          depending on usage levels. Your dedicated account manager will work with you 
          to create a custom rate structure that aligns with your transportation needs and budget.
        </Typography>
      )
    }
  ];

  // Handle category expansion
  const handleCategoryChange = (category: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategory(isExpanded ? category : false);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Filter and group FAQ items
  const filteredItems = searchQuery
    ? faqItems.filter(item => 
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase()))
    : faqItems;

  // Group by category
  const categories = Array.from(new Set(faqItems.map(item => item.category)));
  
  return (
    <>
      <SEO 
        pathname="/faqs" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
        }}
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 10, md: 12 },
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to right, rgba(26, 43, 60, 0.9) 0%, rgba(26, 43, 60, 0.8) 50%, rgba(26, 43, 60, 0.7) 100%)`,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.1))',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 1,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              FAQS
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
            Frequently Asked Questions
          </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: 800,
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
            Find answers to common questions about our transportation services
          </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8, position: 'relative', zIndex: 1 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link 
            component={RouterLink} 
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary">FAQs</Typography>
        </Breadcrumbs>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Search Bar */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              mb: 4,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="h4" gutterBottom>
              Search FAQs
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for keywords..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Paper>

          {/* FAQ Categories and Questions */}
          <GridContainer spacing={4}>
            <GridItem xs={12} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  position: 'sticky',
                  top: 20,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Categories
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {categories.map(category => (
                    <Button
                      key={category}
                      variant={expandedCategory === category ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => setExpandedCategory(category)}
                      sx={{ justifyContent: 'flex-start', textTransform: 'capitalize' }}
                    >
                      {category}
                    </Button>
                  ))}
                </Box>
                
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Need more help?
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ContactSupportIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={12} md={9}>
              {searchQuery && filteredItems.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <HelpOutlineIcon color="primary" sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
                  <Typography variant="h5" gutterBottom>
                    No results found
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    We couldn't find any FAQs matching your search query.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    color="primary"
                  >
                    Contact Our Support Team
                  </Button>
                </Paper>
              ) : (
                categories.map(category => {
                  const categoryItems = filteredItems.filter(item => item.category === category);
                  
                  // Skip categories with no items after filtering
                  if (categoryItems.length === 0) return null;
                  
                  return (
                    <Accordion
                      key={category}
                      expanded={expandedCategory === category}
                      onChange={handleCategoryChange(category)}
                      sx={{ 
                        mb: 2,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        '&:before': {
                          display: 'none',
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${category}-content`}
                        id={`${category}-header`}
                        sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            color: 'white',
                          }
                        }}
                      >
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                          {category} Questions
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {categoryItems.map((item, index) => (
                          <Accordion key={item.id} sx={{ boxShadow: 'none' }}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={`${item.id}-content`}
                              id={`${item.id}-header`}
                              sx={{ 
                                borderBottom: index < categoryItems.length - 1 ? 1 : 0,
                                borderColor: 'divider',
                              }}
                            >
                              <Typography fontWeight={500}>{item.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: 'grey.50', pt: 2 }}>
                              {item.answer}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
              
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  mt: 4,
                  textAlign: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Typography variant="h5" gutterBottom>
                  Still have questions?
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Our customer support team is available 24/7 to assist you with any questions or concerns.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ContactSupportIcon />}
                  sx={{ mt: 1 }}
                >
                  Get in Touch
                </Button>
              </Paper>
            </GridItem>
          </GridContainer>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default FaqPage; 